// Shared code for TextField and TextArea
import classNames from 'classnames'
import { ComponentChild, h, JSX } from 'preact'
import { Ref, useRef, useState } from 'preact/hooks'

import { LbCloseIcon } from '../icon/icon'

export interface TextInputProps {
    modifier?: string
    class?: string
    id: string
    label?: string
    labelClass?: string
    fieldRef?: Ref<HTMLInputElement & HTMLTextAreaElement>
    value: string
    isInverse?: boolean
    hiddenLabel?: boolean
    isInvalid?: boolean
    onErrorClick?: () => void
    isDisabled?: boolean
    isRequired?: boolean
    error?: string
    description?: string
}

interface TextInputWrapperProps extends TextInputProps {
    renderInput: (params: {
        ref: Ref<HTMLInputElement & HTMLTextAreaElement>
        onFocus: () => void
        onBlur: () => void
    }) => ComponentChild
}

export const TextInputWrapper = (props: TextInputWrapperProps): JSX.Element => {
    const {
        modifier,
        id,
        label,
        labelClass,
        fieldRef,
        value,
        isInverse,
        hiddenLabel,
        isInvalid,
        onErrorClick,
        isDisabled,
        isRequired,
        error,
        description,
        renderInput,
    } = props

    const [isFocused, setFocused] = useState(false)
    const blurTimeout = useRef(0)
    const localFieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)

    // Use local ref if no ref was passed in via props
    const effectiveFieldRef = fieldRef ?? localFieldRef

    const className = classNames('form-textfield', modifier, props.class, {
        'form-textfield--inverse': isInverse,
        'form-textfield--hidden-label': hiddenLabel,
        'is-invalid': error || isInvalid,
        'is-disabled': isDisabled,
        'is-focused': isFocused,
        'is-dirty': value,
    })

    const renderError = () => {
        const errorProps: JSX.HTMLAttributes<HTMLDivElement> = {
            class: 'form-textfield__error',
        }

        if (isInverse) {
            errorProps.onClick = (event) => {
                event.preventDefault()
                effectiveFieldRef.current?.focus()
                onErrorClick?.()
            }
        }

        return (
            <div {...errorProps}>
                <LbCloseIcon class="form-textfield__error-icon" />
                {error}
            </div>
        )
    }

    return (
        <div class={className}>
            <div class="form-textfield__inner">
                {renderInput({
                    ref: effectiveFieldRef,
                    onFocus: () => {
                        window.clearTimeout(blurTimeout.current)
                        setFocused(true)
                    },
                    onBlur: () => {
                        window.clearTimeout(blurTimeout.current)
                        blurTimeout.current = window.setTimeout(() => setFocused(false), 100)
                    },
                })}
                <label class={classNames('form-textfield__label', labelClass)} for={id}>
                    {label}
                    {isRequired && ' *'}
                </label>
            </div>
            {(error || isInvalid) && renderError()}
            {description && <div class="form-textfield__description">{description}</div>}
        </div>
    )
}
